import React from 'react';
import type { RunSummary } from '@root/lib';
import { formatMoney } from '@shared/lib';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { runResultLabel, signedMoney } from './runLabels';
import styles from './RunList.module.scss';

interface RunRowProps {
  run: RunSummary;
  symbol: string;
  onView: (run: RunSummary) => void;
}

// One run per row. The table only appears where there is room for it.
const RunRow: React.FC<RunRowProps> = ({ run, symbol, onView }) => {
  const { t } = useTranslation();
  const net = formatMoney(symbol, Math.abs(run.net));

  return (
    <button type="button" className={styles.row} onClick={() => onView(run)}>
      <span className={cn(styles.cell, styles.cell_round)}>
        {run.roundLabel}
      </span>
      <span className={cn(styles.cell, styles.cell_time)}>
        {new Date(run.startedAt).toLocaleString(undefined, {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </span>
      <span className={cn(styles.cell, styles.cell_amount)}>
        {formatMoney(symbol, run.rollAmount)}
      </span>
      <span className={cn(styles.cell, styles.cell_rolls)}>
        {run.paidRolls}
      </span>
      <span className={cn(styles.cell, styles.cell_spent)}>
        {formatMoney(symbol, run.totalSpent)}
      </span>
      <span className={cn(styles.cell, styles.cell_multipliers)}>
        {run.topMultiplier ? `${run.topMultiplier}X` : '—'}
      </span>
      <span className={cn(styles.cell, styles.cell_result, styles.strong)}>
        {runResultLabel(run.result, t)}
      </span>
      <span className={cn(styles.cell, styles.cell_win)}>
        {formatMoney(symbol, run.totalWin)}
      </span>
      <span
        className={cn(styles.cell, styles.cell_net, styles.strong, {
          [styles.net_up]: run.net > 0,
          [styles.net_down]: run.net < 0,
        })}
      >
        {signedMoney(net, run.net)}
      </span>
    </button>
  );
};

export default RunRow;
